/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { useURLState } from '../hooks/useURLState'
import {
  clearStoredAccountId,
  getStoredAccountId,
  setStoredAccountId,
} from '../utils/accountStorage'

export const AccountContext = createContext(null)

const ACCOUNT_URL_KEY = 'account'

function isBrowser() {
  return typeof window !== 'undefined'
}

function emitAccountChange(accountId) {
  if (!isBrowser()) {
    return
  }

  try {
    window.dispatchEvent(
      new CustomEvent('account:changed', {
        detail: accountId || null,
      })
    )
  } catch (error) {
    console.warn('Failed to dispatch account change event', error)
  }
}

export function AccountProvider({ children, persistenceMode = 'url' }) {
  const { user, isAuthenticated, loading: authLoading } = useAuth()
  const [accounts, setAccounts] = useState([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [isChangingAccount, setIsChangingAccount] = useState(false)
  const [error, setError] = useState(null)

  const [urlAccountId, setUrlAccountId] = useURLState(
    ACCOUNT_URL_KEY,
    null
  )

  // Initialize from localStorage immediately to avoid race conditions
  const [selectedAccountId, setSelectedAccountId] = useState(() => {
    return getStoredAccountId()
  })

  const persistedAccountId = useMemo(() => {
    if (persistenceMode === 'url' && urlAccountId) {
      return urlAccountId
    }
    return getStoredAccountId()
  }, [persistenceMode, urlAccountId])

  const selectedAccount = useMemo(() => {
    if (!selectedAccountId) {
      return null
    }

    return accounts.find((account) => account.id === selectedAccountId) || null
  }, [accounts, selectedAccountId])

  // Accounts whose Instagram connection needs re-authentication.
  // Derived from the `needs_reconnect` flag returned by GET /accounts.
  // Exposed as durable context state (NOT a one-shot event) so the global
  // InstagramReconnectModal can read it after the App-level loading spinner
  // unmounts — an event fired during initial load would otherwise be lost.
  const accountsNeedingReconnect = useMemo(
    () => accounts.filter((account) => account?.needs_reconnect),
    [accounts]
  )

  const applySelection = useCallback(
    (accountId) => {
      setSelectedAccountId(accountId || null)

      if (persistenceMode === 'url') {
        const nextValue = accountId || null
        if (nextValue !== urlAccountId) {
          setUrlAccountId(nextValue)
        }
      }
    },
    [persistenceMode, setUrlAccountId, urlAccountId]
  )

  const ensureSelectedAccount = useCallback(
    (accountList) => {
      if (!Array.isArray(accountList) || accountList.length === 0) {
        if (selectedAccountId !== null) {
          applySelection(null)
        }
        return
      }

      if (
        selectedAccountId &&
        accountList.some((account) => account.id === selectedAccountId)
      ) {
        return
      }

      if (
        persistedAccountId &&
        accountList.some((account) => account.id === persistedAccountId)
      ) {
        applySelection(persistedAccountId)
        return
      }

      applySelection(accountList[0].id)
    },
    [applySelection, persistedAccountId, selectedAccountId]
  )

  const refreshAccounts = useCallback(async () => {
    if (!user) {
      setAccounts([])
      applySelection(null)
      return []
    }

    setIsLoadingAccounts(true)
    setError(null)

    try {
      const response = await api.getInstagramAccounts()

      const accountList = Array.isArray(response?.accounts)
        ? response.accounts
        : Array.isArray(response)
          ? response
          : []

      setAccounts(accountList)
      ensureSelectedAccount(accountList)
      return accountList
    } catch (err) {
      console.error('Failed to load Instagram accounts:', err)
      setError(err)
      setAccounts([])
      applySelection(null)
      return []
    } finally {
      setIsLoadingAccounts(false)
    }
  }, [applySelection, ensureSelectedAccount, user])

  const selectAccount = useCallback(
    async (accountId) => {
      setIsChangingAccount(true)
      try {
        applySelection(accountId)
      } finally {
        setIsChangingAccount(false)
      }
    },
    [applySelection]
  )

  const removeAccount = useCallback(
    async (accountId) => {
      try {
        await api.disconnectInstagramAccount(accountId)
        const updatedAccounts = await refreshAccounts()

        if (!updatedAccounts.some((account) => account.id === selectedAccountId)) {
          applySelection(updatedAccounts[0]?.id || null)
        }
      } catch (err) {
        console.error('Failed to remove account:', err)
        throw err
      }
    },
    [applySelection, refreshAccounts, selectedAccountId]
  )

  const value = useMemo(
    () => ({
      accounts,
      accountsNeedingReconnect,
      error,
      selectedAccount,
      selectedAccountId,
      isLoadingAccounts,
      isChangingAccount,
      hasAccounts: accounts.length > 0,
      selectAccount,
      refreshAccounts,
      removeAccount,
      addAccount: () => {}, // Placeholder - OAuth flow handled separately
      persistenceMode,
    }),
    [
      accounts,
      accountsNeedingReconnect,
      error,
      isChangingAccount,
      isLoadingAccounts,
      persistenceMode,
      refreshAccounts,
      removeAccount,
      selectAccount,
      selectedAccount,
      selectedAccountId,
    ]
  )

  useEffect(() => {
    if (!selectedAccountId) {
      clearStoredAccountId()
      return
    }

    setStoredAccountId(selectedAccountId)
  }, [selectedAccountId])

  useEffect(() => {
    emitAccountChange(selectedAccountId)
  }, [selectedAccountId])

  useEffect(() => {
    // Don't clear account while auth is still loading
    if (authLoading) {
      return
    }

    if (!isAuthenticated) {
      setAccounts([])
      applySelection(null)
      clearStoredAccountId()
      return
    }

    refreshAccounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading])

  useEffect(() => {
    if (!persistedAccountId) {
      return
    }

    // Only apply persisted account if it exists in the accounts list
    const accountExists = accounts.some(acc => acc.id === persistedAccountId)
    if (!accountExists) {
      return
    }

    if (persistedAccountId !== selectedAccountId) {
      applySelection(persistedAccountId)
    }
  }, [accounts, applySelection, persistedAccountId, selectedAccountId])

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  )
}
